import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { authFetch } from "../../auth";
import {
  buildGlobalIntroParagraphs,
  buildGroupIntroNarrative,
  buildQuestionNarrative,
} from "../../utils/statsNarrative";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { uem_logo } from "../../constants/uemLogo";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.alumni.uem.mz";
const BUILDER_BASE = `${API_BASE}/api/v1/builder`;
/** Paleta institucional com mais variedade (verdes + complementares) */
const CHART_PALETTE = [
  "#16a34a",
  "#0d9488",
  "#2563eb",
  "#ca8a04",
  "#7c3aed",
  "#059669",
  "#0ea5e9",
  "#ea580c",
  "#db2777",
  "#4f46e5",
  "#65a30d",
  "#14b8a6",
  "#6366f1",
  "#f59e0b",
  "#22c55e",
];
const CHART_HEIGHT_CLASS = "h-48 sm:h-52";
const PRINT_CHART_HEIGHT_CLASS = "h-56";
const RADAR_HEIGHT_CLASS = "h-56 sm:h-60";
const PRINT_RADAR_HEIGHT_CLASS = "h-64";

const nullTooltip = () => null;

/** Rótulos amigáveis em PT para chaves vindas da API (evita "count" na interface). */
function labelForDataKey(dataKey) {
  switch (dataKey) {
    case "count":
      return "Quantidade de respostas";
    case "percentage":
      return "Percentagem";
    case "value":
      return "Valor";
    default:
      return String(dataKey);
  }
}

function BarTooltipPt({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0];
  const data = row.payload || {};
  const titulo = data.label != null ? String(data.label) : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      {titulo && <div className="font-semibold text-gray-900 mb-1.5 border-b border-gray-100 pb-1">{titulo}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="text-gray-700">
          <span className="text-gray-500">{labelForDataKey(entry.dataKey)}:</span>{" "}
          {entry.dataKey === "percentage" && typeof entry.value === "number"
            ? `${entry.value}%`
            : entry.value}
        </div>
      ))}
    </div>
  );
}

function PieTooltipPt({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0];
  const data = row.payload || {};
  const nome = data.label != null ? String(data.label) : row.name != null ? String(row.name) : "Opção";
  const qtd = data.count ?? row.value;
  const pct = typeof data.percentage === "number" ? data.percentage : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <div className="font-semibold text-gray-900 mb-1">{nome}</div>
      <div className="text-gray-700">
        <span className="text-gray-500">Quantidade de respostas:</span> {qtd ?? "—"}
      </div>
      {pct != null && (
        <div className="text-gray-700 mt-0.5">
          <span className="text-gray-500">Percentagem:</span> {pct}%
        </div>
      )}
    </div>
  );
}

function RadarTooltipPt({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      {label != null && <div className="font-semibold text-gray-900 mb-1.5">{String(label)}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="text-gray-700">
          <span className="text-gray-500">{entry.name || "Série"}:</span> {entry.value}
        </div>
      ))}
    </div>
  );
}

function Card({ title, right, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm sm:text-base font-semibold text-gray-800">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-emerald-700">{label}</div>
      <div className="text-lg sm:text-xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function OptionsLegend({ options }) {
  if (!Array.isArray(options) || !options.length) return null;
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((o, idx) => (
        <div key={o.option_id || `${o.label}-${idx}`} className="rounded-md border border-gray-100 px-2.5 py-1.5 text-xs">
          <div className="font-medium text-gray-700 truncate">{o.label}</div>
          <div className="text-gray-500">{o.count ?? 0} respostas {typeof o.percentage === "number" ? `• ${o.percentage}%` : ""}</div>
        </div>
      ))}
    </div>
  );
}

function StatMeta({ stats }) {
  if (!stats) return null;
  return (
    <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 mt-2">
      <span>Respondidas: {stats.total_answered ?? 0}</span>
      <span>Ignoradas: {stats.skipped ?? 0}</span>
      {typeof stats.mean === "number" && <span>Média: {stats.mean}</span>}
      {typeof stats.mean === "number" && <span>Média: {stats.mean}</span>}
      {typeof stats.median === "number" && <span>Mediana: {stats.median}</span>}
      {typeof stats.min === "number" && <span>Min: {stats.min}</span>}
      {typeof stats.max === "number" && <span>Max: {stats.max}</span>}
      {typeof stats.std_dev === "number" && <span>Desvio padrão: {stats.std_dev}</span>}
      {typeof stats.std_dev === "number" && <span>Desvio padrão: {stats.std_dev}</span>}
    </div>
  );
}

function renderQuestionChart(block, opts = {}) {
  const {
    openOnlySamples = false,
    forPrint = false,
    chartHeightClass = CHART_HEIGHT_CLASS,
    openPreviewLimit = 6,
    onOpenViewMore = null,
  } = opts;
  const chartType = block?.chart_type;
  const data = block?.data || {};
  const pieTip = forPrint ? nullTooltip : <PieTooltipPt />;
  const barTip = forPrint ? nullTooltip : <BarTooltipPt />;

  if (chartType === "pie_or_bar" || chartType === "horizontal_bar") {
    const options = Array.isArray(data.options) ? data.options : [];
    if (!options.length) return <div className="text-sm text-gray-500">Sem dados.</div>;
    if (chartType === "pie_or_bar") {
      return (
        <div>
          <div className={chartHeightClass}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={options} dataKey="count" nameKey="label" outerRadius="78%">
                  {options.map((o, idx) => (
                    <Cell key={o.option_id || o.label || idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip content={pieTip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {!forPrint && <OptionsLegend options={options} />}
        </div>
      );
    }
    return (
      <div>
        <div className={chartHeightClass}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={options} layout="vertical" margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="label" type="category" width={120} tick={{ fontSize: 10 }} />
              <Tooltip content={barTip} cursor={forPrint ? false : { fill: "rgba(22, 163, 74, 0.06)" }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {options.map((_, idx) => (
                  <Cell key={idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {!forPrint && <OptionsLegend options={options} />}
      </div>
    );
  }

  if (chartType === "histogram") {
    const distribution = Array.isArray(data.distribution) ? data.distribution : [];
    if (!distribution.length) return <div className="text-sm text-gray-500">Sem dados.</div>;
    return (
      <div className={chartHeightClass}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={distribution} margin={{ left: 4, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} height={36} />
            <YAxis tick={{ fontSize: 11 }} width={32} />
            <Tooltip content={barTip} cursor={forPrint ? false : { fill: "rgba(22, 163, 74, 0.06)" }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {distribution.map((_, idx) => (
                <Cell key={idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === "word_cloud_or_list") {
    const samples = Array.isArray(data.sample_responses) ? data.sample_responses : [];
    const limit =
      !forPrint && typeof openPreviewLimit === "number" && openPreviewLimit > 0
        ? openPreviewLimit
        : samples.length;
    const clipped = !openOnlySamples ? samples : samples.slice(0, limit);
    const totalAnswered =
      typeof block?.stats?.total_answered === "number" ? block.stats.total_answered : null;
    const remaining = openOnlySamples
      ? Math.max(0, (totalAnswered ?? samples.length) - clipped.length)
      : 0;
    return (
      <div className="space-y-3">
        {!openOnlySamples && !forPrint && (
          <div className="text-xs text-gray-500">Resumo textual da pergunta aberta.</div>
        )}
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Algumas respostas a pergunta</div>
          {!clipped.length ? (
            <div className="text-sm text-gray-500">Sem exemplos.</div>
          ) : (
            <ul className="space-y-1">
              {clipped.map((s, idx) => (
                <li key={idx} className="text-sm text-gray-700 border-l-2 border-emerald-200 pl-2">
                  {String(s)}
                </li>
              ))}
            </ul>
          )}
          {remaining > 0 && typeof onOpenViewMore === "function" && (
            <button
              type="button"
              onClick={() =>
                onOpenViewMore({
                  questionId: block?.question_id,
                  label: block?.label || "Pergunta",
                  alreadyShown: clipped.length,
                  totalAnswered,
                })
              }
              className="mt-2 text-xs text-emerald-700 hover:underline"
            >
              Ver mais {remaining} respostas
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      Este tipo de gráfico não é suportado: <span className="font-mono">{String(chartType || "desconhecido")}</span>
    </div>
  );
}

function GroupRadarChart({ agg, heightClass, forPrint }) {
  const radarData = Array.isArray(agg?.labels)
    ? agg.labels.map((label, i) => {
        const point = { label };
        (Array.isArray(agg.series) ? agg.series : []).forEach((s) => {
          point[s.label || "Serie"] = (Array.isArray(s.values) ? s.values[i] : 0) ?? 0;
        });
        return point;
      })
    : [];
  if (!radarData.length) return null;
  const radarTip = forPrint ? nullTooltip : <RadarTooltipPt />;
  return (
    <div className={`${heightClass} mb-4`}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={radarData}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="label" tick={{ fontSize: 10 }} />
          <PolarRadiusAxis tick={{ fontSize: 10 }} />
          {(agg.series || []).map((s, sIdx) => (
            <Radar
              key={`${s.label || "serie"}-${sIdx}`}
              name={s.label || `Dimensão ${sIdx + 1}`}
              dataKey={s.label || "Serie"}
              stroke={CHART_PALETTE[sIdx % CHART_PALETTE.length]}
              fill={CHART_PALETTE[sIdx % CHART_PALETTE.length]}
              fillOpacity={0.22}
            />
          ))}
          <Tooltip content={radarTip} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function StatisticsPage() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("charts");
  const [filterOptions, setFilterOptions] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filters, setFilters] = useState({
    gender: "",
    organic_unit_name: "",
    course_name: "",
    last_year: "",
    regime: "",
    year_entry: "",
    birth_province_id: "",
    birth_country_id: "",
  });
  const [openMore, setOpenMore] = useState(null); // { questionId, label, alreadyShown, totalAnswered }
  const [openMoreLoading, setOpenMoreLoading] = useState(false);
  const [openMoreError, setOpenMoreError] = useState("");
  const [openMoreItems, setOpenMoreItems] = useState([]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  };

  const loadForms = useCallback(async () => {
    setLoadingForms(true);
    setError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/`);
      if (!resp.ok) throw new Error(`Erro ${resp.status} ao carregar formulários`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : [];
      setForms(list);
      if (!selectedFormId && list.length) setSelectedFormId(String(list[0].id));
    } catch (e) {
      setError(e?.message || "Falha ao carregar formulários");
    } finally {
      setLoadingForms(false);
    }
  }, [selectedFormId]);

  const buildFiltersQuery = useCallback((f) => {
    const p = new URLSearchParams();
    Object.entries(f || {}).forEach(([k, v]) => {
      if (v == null) return;
      const s = String(v).trim();
      if (!s) return;
      p.set(k, s);
    });
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  }, []);

  const loadFilterOptions = useCallback(async (formId) => {
    if (!formId) return;
    setLoadingFilters(true);
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/${formId}/stats/filters/`);
      if (!resp.ok) throw new Error(`Erro ${resp.status} ao carregar filtros`);
      const data = await resp.json();
      setFilterOptions(data && typeof data === "object" ? data : null);
    } catch (_) {
      setFilterOptions(null);
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const loadStats = useCallback(async (formId, f) => {
    if (!formId) return;
    setLoadingStats(true);
    setError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/${formId}/stats/${buildFiltersQuery(f)}`);
      if (!resp.ok) {
        if (resp.status === 404) throw new Error("Formulário não encontrado.");
        if (resp.status === 401 || resp.status === 403) throw new Error("Sem permissão para ver estas estatísticas.");
        throw new Error(`Erro ${resp.status} ao carregar estatísticas`);
      }
      const data = await resp.json();
      setStatsData(data);
    } catch (e) {
      setError(e?.message || "Falha ao carregar estatísticas");
      setStatsData(null);
    } finally {
      setLoadingStats(false);
    }
  }, [buildFiltersQuery]);

  const loadOpenAnswers = useCallback(
    async ({ formId, questionId }) => {
      if (!questionId) throw new Error("Pergunta inválida.");
      const p = new URLSearchParams();
      p.set("question", String(questionId));
      // Tentativa de restringir pelo formulário (nem sempre existe no backend).
      if (formId) p.set("form", String(formId));
      // Alguns backends DRF aceitam page_size.
      p.set("page_size", "500");
      const resp = await authFetch(`${BUILDER_BASE}/answers/?${p.toString()}`);
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao carregar respostas`);
      }
      const data = await resp.json();
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      return list;
    },
    []
  );

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  useEffect(() => {
    if (selectedFormId) loadStats(selectedFormId, filters);
  }, [selectedFormId, loadStats, filters]);

  useEffect(() => {
    if (!selectedFormId) return;
    setFilters({
      gender: "",
      organic_unit_name: "",
      course_name: "",
      last_year: "",
      regime: "",
      year_entry: "",
      birth_province_id: "",
      birth_country_id: "",
    });
    loadFilterOptions(selectedFormId);
  }, [selectedFormId, loadFilterOptions]);

  const meta = statsData?.meta || {};
  const questionBlocks = useMemo(
    () => (Array.isArray(statsData?.questions) ? statsData.questions : []),
    [statsData]
  );

  const generatedAt = useMemo(() => {
    if (!meta.generated_at) return "-";
    const d = new Date(meta.generated_at);
    if (Number.isNaN(d.getTime())) return meta.generated_at;
    return d.toLocaleString("pt-PT");
  }, [meta.generated_at]);

  const targetGroupLabel = useMemo(() => {
    const raw = String(meta.target_group || "").toLowerCase();
    if (raw === "graduate" || raw === "alumni") return "Graduados";
    if (raw === "employer") return "Empregadores";
    return meta.target_group || "-";
  }, [meta.target_group]);

  const openCount = useMemo(() => {
    return questionBlocks.reduce((acc, block) => {
      const isGroup = !!block.super_question_id || block.chart_type === "group";
      if (!isGroup) return acc + (block.chart_type === "word_cloud_or_list" ? 1 : 0);
      const nested = Array.isArray(block.questions) ? block.questions : [];
      return acc + nested.filter((q) => q.chart_type === "word_cloud_or_list").length;
    }, 0);
  }, [questionBlocks]);

  const chartCount = useMemo(() => {
    return questionBlocks.reduce((acc, block) => {
      const isGroup = !!block.super_question_id || block.chart_type === "group";
      if (!isGroup) return acc + (block.chart_type !== "word_cloud_or_list" ? 1 : 0);
      const nested = Array.isArray(block.questions) ? block.questions : [];
      const nestedCharts = nested.filter((q) => q.chart_type !== "word_cloud_or_list").length;
      return acc + nestedCharts + (block.aggregated?.available ? 1 : 0);
    }, 0);
  }, [questionBlocks]);

  const introParagraphs = useMemo(
    () =>
      buildGlobalIntroParagraphs(statsData?.meta || {}, {
        generatedAtLabel: generatedAt,
        targetGroupLabel,
      }),
    [statsData, generatedAt, targetGroupLabel]
  );

  const printTitle = useMemo(
    () =>
      `Estatisticas-${meta.form_code || meta.form_id || selectedFormId || "formulario"}`,
    [meta.form_code, meta.form_id, selectedFormId]
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printTitle,
    pageStyle: `
      @page { size: A4; margin: 14mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        /* Conteúdo off-screen (fixed + left negativo) fica fora da página no iframe de impressão */
        .stats-print-root {
          color: #111827 !important;
          background: #fff !important;
          position: static !important;
          left: auto !important;
          top: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          transform: none !important;
          visibility: visible !important;
        }
        .stats-print-block { break-inside: avoid; page-break-inside: avoid; }
      }
    `,
  });

  return (
    <div className="min-h-screen max-w-7xl mx-auto pt-3 md:pt-4 pb-28 md:pb-24 px-4 sm:px-5 md:px-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-xs sm:text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:border-emerald-400 hover:text-emerald-700 transition-colors"
          >
            ← Voltar
          </button>
          <h1 className="mt-3 text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Estatísticas</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Resumo das respostas do formulário
          </p>
        </div>
      </div>

      <Card
        title="Formulário e actualização"
        right={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <button
              type="button"
              onClick={() => handlePrint()}
              disabled={!statsData || loadingStats}
              className="text-xs px-3 py-1.5 rounded-lg border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Abre a janela para imprimir — depois escolha Guardar como PDF"
            >
              Gerar PDF
            </button>
            <button
              type="button"
              onClick={() => loadStats(selectedFormId)}
              disabled={!selectedFormId || loadingStats}
              className="text-xs text-emerald-700 hover:underline disabled:text-gray-400"
            >
              {loadingStats ? "A carregar..." : "Actualizar"}
            </button>
          </div>
        }
      >
        <div className="mb-3 rounded-lg border border-gray-100 bg-white px-3 py-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-700 flex flex-col">
              <span className="mb-1">Formulário</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                value={selectedFormId}
                onChange={(e) => setSelectedFormId(e.target.value)}
                disabled={loadingForms || !forms.length}
              >
                {!forms.length && <option value="">{loadingForms ? "A carregar..." : "Sem formulários"}</option>}
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title} ({f.code})
                  </option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[160px]">
              <span className="mb-1">Sexo</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.gender}
                onChange={(e) => setFilters((p) => ({ ...p, gender: e.target.value }))}
                disabled={!filterOptions?.gender?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todos"}</option>
                {(filterOptions?.gender || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[220px]">
              <span className="mb-1">Unidade</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.organic_unit_name}
                onChange={(e) => setFilters((p) => ({ ...p, organic_unit_name: e.target.value }))}
                disabled={!filterOptions?.organic_unit_name?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todas"}</option>
                {(filterOptions?.organic_unit_name || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[220px]">
              <span className="mb-1">Curso</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.course_name}
                onChange={(e) => setFilters((p) => ({ ...p, course_name: e.target.value }))}
                disabled={!filterOptions?.course_name?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todos"}</option>
                {(filterOptions?.course_name || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[140px]">
              <span className="mb-1">Ano (fim)</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.last_year}
                onChange={(e) => setFilters((p) => ({ ...p, last_year: e.target.value }))}
                disabled={!filterOptions?.last_year?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todos"}</option>
                {(filterOptions?.last_year || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[140px]">
              <span className="mb-1">Regime</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.regime}
                onChange={(e) => setFilters((p) => ({ ...p, regime: e.target.value }))}
                disabled={!filterOptions?.regime?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todos"}</option>
                {(filterOptions?.regime || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-700 flex flex-col min-w-[140px]">
              <span className="mb-1">Ano (entrada)</span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50"
                value={filters.year_entry}
                onChange={(e) => setFilters((p) => ({ ...p, year_entry: e.target.value }))}
                disabled={!filterOptions?.year_entry?.length || loadingFilters}
              >
                <option value="">{loadingFilters ? "A carregar..." : "Todos"}</option>
                {(filterOptions?.year_entry || []).map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap items-center gap-2 pb-0.5">
              <button
                type="button"
                onClick={() => loadStats(selectedFormId, filters)}
                disabled={!selectedFormId || loadingStats}
                className="text-xs px-3 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aplicar filtros
              </button>
              <button
                type="button"
                onClick={() => {
                  const cleared = {
                    gender: "",
                    organic_unit_name: "",
                    course_name: "",
                    last_year: "",
                    regime: "",
                    year_entry: "",
                    birth_province_id: "",
                    birth_country_id: "",
                  };
                  setFilters(cleared);
                  loadStats(selectedFormId, cleared);
                }}
                disabled={!selectedFormId || loadingStats}
                className="text-xs px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Limpar filtros
              </button>
              {meta?.is_filtered && (
                <div className="text-[11px] text-emerald-700">
                  A ver resultados filtrados.
                </div>
              )}
            </div>
          </div>
        </div>

        {meta?.is_filtered && meta?.filters_applied && (
          <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <span className="font-medium">Filtros aplicados:</span>{" "}
            {Object.entries(meta.filters_applied)
              .filter(([, v]) => v != null && String(v) !== "")
              .map(([k, v]) => `${k}=${v}`)
              .join(" • ") || "—"}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total de respostas" value={meta.total_responses ?? 0} />
          <Kpi label="Grupo alvo" value={targetGroupLabel} />
          <Kpi label="Gerado em" value={generatedAt} />
          <Kpi label="Blocos de gráficos" value={questionBlocks.length} />
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </Card>

      <div className="mt-5 space-y-4">
        <Card title="Categorias">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 gap-1">
            <button
              type="button"
              onClick={() => setActiveTab("charts")}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition ${
                activeTab === "charts"
                  ? "bg-emerald-500 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Indicadores e gráficos ({chartCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("open")}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition ${
                activeTab === "open"
                  ? "bg-emerald-500 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Perguntas abertas ({openCount})
            </button>
          </div>
        </Card>

        {!loadingStats && !error && selectedFormId && questionBlocks.length === 0 && (
          <Card title="Sem dados">
            <div className="text-sm text-gray-500">Este formulário ainda não tem estatísticas.</div>
          </Card>
        )}

        {questionBlocks.map((block, idx) => {
          const isGroup = !!block.super_question_id || block.chart_type === "group";
          if (isGroup) {
            const agg = block.aggregated || {};
            const radarData = Array.isArray(agg.labels)
              ? agg.labels.map((label, i) => {
                  const point = { label };
                  (Array.isArray(agg.series) ? agg.series : []).forEach((s) => {
                    point[s.label || "Serie"] = (Array.isArray(s.values) ? s.values[i] : 0) ?? 0;
                  });
                  return point;
                })
              : [];
            const nested = Array.isArray(block.questions) ? block.questions : [];
            const nestedFiltered = nested.filter((q) =>
              activeTab === "open"
                ? q.chart_type === "word_cloud_or_list"
                : q.chart_type !== "word_cloud_or_list"
            );
            const shouldShowGroup =
              nestedFiltered.length > 0 || (activeTab === "charts" && agg.available && radarData.length > 0);
            if (!shouldShowGroup) return null;
            return (
              <Card key={`group-${block.super_question_id || idx}`} title={block.label || "Grupo"}>
                {activeTab === "charts" && agg.available && radarData.length > 0 && (
                  <GroupRadarChart agg={agg} heightClass={RADAR_HEIGHT_CLASS} forPrint={false} />
                )}
                {!agg.available && (
                  <div className="text-sm text-gray-500 mb-3">
                    Agregado indisponivel. Exibindo apenas sub-questoes.
                  </div>
                )}
                <div className="space-y-3">
                  {nestedFiltered.map((q) => (
                    <div key={q.question_id} className="rounded-lg border border-gray-100 p-3">
                      <div className="text-sm font-medium text-gray-900">{q.label}</div>
                      <StatMeta stats={q.stats} />
                      <div className="mt-3">{renderQuestionChart(q, { openOnlySamples: activeTab === "open" })}</div>
                    </div>
                  ))}
                  {nestedFiltered.length === 0 && (
                    <div className="text-sm text-gray-500">Grupo sem sub-questoes.</div>
                  )}
                </div>
              </Card>
            );
          }

          const isOpen = block.chart_type === "word_cloud_or_list";
          if (activeTab === "open" && !isOpen) return null;
          if (activeTab === "charts" && isOpen) return null;

          return (
            <Card key={`q-${block.question_id || idx}`} title={block.label || "Questão"}>
              <StatMeta stats={block.stats} />
              <div className="mt-3">
                {renderQuestionChart(block, {
                  openOnlySamples: activeTab === "open",
                  openPreviewLimit: 6,
                  onOpenViewMore: async ({ questionId, label, alreadyShown, totalAnswered }) => {
                    setOpenMore({ questionId, label, alreadyShown, totalAnswered });
                    setOpenMoreItems([]);
                    setOpenMoreError("");
                    setOpenMoreLoading(true);
                    try {
                      const list = await loadOpenAnswers({ formId: selectedFormId, questionId });
                      const items = list
                        .map((a) => a?.text_answer)
                        .filter((v) => v != null && String(v).trim() !== "")
                        .map((v) => String(v));
                      setOpenMoreItems(items);
                    } catch (e) {
                      setOpenMoreError(e?.message || "Falha ao carregar respostas.");
                    } finally {
                      setOpenMoreLoading(false);
                    }
                  },
                })}
              </div>
            </Card>
          );
        })}

        {!loadingStats && !error && selectedFormId && questionBlocks.length > 0 && (
          activeTab === "open" && openCount === 0 ? (
            <Card title="Perguntas abertas">
              <div className="text-sm text-gray-500">Não há perguntas abertas neste formulário.</div>
            </Card>
          ) : activeTab === "charts" && chartCount === 0 ? (
            <Card title="Indicadores e gráficos">
              <div className="text-sm text-gray-500">Não há gráficos neste formulário.</div>
            </Card>
          ) : null
        )}
      </div>

      {openMore && (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{openMore.label}</div>
                <div className="text-xs text-gray-500">
                  {openMoreLoading
                    ? "A carregar..."
                    : `${openMoreItems.length} respostas`}
                  {typeof openMore.totalAnswered === "number" ? ` (total: ${openMore.totalAnswered})` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenMore(null);
                  setOpenMoreItems([]);
                  setOpenMoreError("");
                  setOpenMoreLoading(false);
                }}
                className="text-xs text-gray-600 hover:underline"
              >
                Fechar
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              {openMoreError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {openMoreError}
                </div>
              ) : openMoreLoading ? (
                <div className="text-sm text-gray-500">A carregar respostas...</div>
              ) : openMoreItems.length === 0 ? (
                <div className="text-sm text-gray-500">Sem respostas.</div>
              ) : (
                <ul className="space-y-2">
                  {openMoreItems.map((s, i) => (
                    <li
                      key={i}
                      className="text-sm text-gray-800 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Relatório para impressão / PDF (fora do ecrã, mantém layout para Recharts) */}
      <div
        ref={printRef}
        className="stats-print-root fixed left-[-9999px] top-0 z-0 w-[210mm] max-w-full bg-white p-8 text-sm text-gray-900 leading-relaxed print:static print:left-auto print:top-auto print:z-auto print:w-full print:max-w-full print:transform-none"
        aria-hidden
      >
        <header className="border-b-2 border-emerald-700 pb-4 mb-6 stats-print-block">
          <div className="flex flex-col items-center text-center mb-5 stats-print-block">
            <img
              src={`data:image/png;base64,${uem_logo}`}
              alt="Universidade Eduardo Mondlane"
              className="h-20 w-auto max-w-full object-contain mx-auto"
            />
            <p className="text-sm font-semibold text-gray-900 mt-3">GaPQEI</p>
            <p className="text-xs text-gray-600 mt-0.5 max-w-md">
              Gabinete de Plano de Qualidade e Ensino Integrado
            </p>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Relatório de estatísticas do inquérito</h1>
          <div className="mt-3 grid gap-1 text-xs text-gray-600">
            <p>
              <span className="font-medium text-gray-700">Inquérito:</span>{" "}
              {meta.form_title ? `"${meta.form_title}"` : "—"}
              {meta.form_code ? ` (${meta.form_code})` : ""}
            </p>
            <p>
              <span className="font-medium text-gray-700">Total de submissões:</span>{" "}
              {meta.total_responses ?? 0}
            </p>
            <p>
              <span className="font-medium text-gray-700">Grupo-alvo:</span> {targetGroupLabel}
            </p>
            <p>
              <span className="font-medium text-gray-700">Dados compilados em:</span> {generatedAt}
            </p>
          </div>
        </header>

        <section className="mb-8 stats-print-block">
          {introParagraphs.map((para, i) => (
            <p key={i} className="mb-3 text-justify text-gray-800">
              {para}
            </p>
          ))}
        </section>

        <section className="space-y-10">
          {questionBlocks.map((block, idx) => {
            const isGroup = !!block.super_question_id || block.chart_type === "group";
            if (isGroup) {
              const agg = block.aggregated || {};
              const nested = Array.isArray(block.questions) ? block.questions : [];
              return (
                <article key={`print-group-${block.super_question_id || idx}`} className="stats-print-block">
                  <h2 className="text-lg font-semibold text-emerald-800 border-b border-emerald-200 pb-2 mb-3">
                    {block.label || "Grupo de perguntas"}
                  </h2>
                  <p className="mb-4 text-justify text-gray-800">{buildGroupIntroNarrative(block)}</p>
                  {agg.available && (
                    <GroupRadarChart agg={agg} heightClass={PRINT_RADAR_HEIGHT_CLASS} forPrint />
                  )}
                  <div className="space-y-8 pl-0">
                    {nested.map((q) => (
                      <div
                        key={q.question_id}
                        className="rounded-lg border border-gray-200 p-4 stats-print-block"
                      >
                        <h3 className="text-base font-medium text-gray-900">{q.label}</h3>
                        <p className="mt-2 text-justify text-gray-800">{buildQuestionNarrative(q)}</p>
                        <div className="mt-2 print:text-xs">
                          <StatMeta stats={q.stats} />
                        </div>
                        <div className="mt-4">
                          {renderQuestionChart(q, {
                            openOnlySamples: true,
                            forPrint: true,
                            chartHeightClass: PRINT_CHART_HEIGHT_CLASS,
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              );
            }

            return (
              <article key={`print-q-${block.question_id || idx}`} className="stats-print-block">
                <h2 className="text-lg font-semibold text-emerald-800 border-b border-emerald-200 pb-2 mb-3">
                  {block.label || "Pergunta"}
                </h2>
                <p className="mb-3 text-justify text-gray-800">{buildQuestionNarrative(block)}</p>
                <div className="mb-3 print:text-xs">
                  <StatMeta stats={block.stats} />
                </div>
                <div>
                  {renderQuestionChart(block, {
                    openOnlySamples: true,
                    forPrint: true,
                    chartHeightClass: PRINT_CHART_HEIGHT_CLASS,
                  })}
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}

